import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import styled from 'styled-components/macro'

import { withApolloV4Provider } from 'utilities/apollo'
import useGpsrDialog from 'hooks/use-gpsr-dialog'

import LoadingIndicator from 'components/atoms/loading-indicator'
import LabeledCheckbox from 'components/atoms/labeled-checkbox'
import Checkbox from 'components/atoms/checkbox'
import Button from 'components/atoms/button'
import Typography from 'components/molecules/typography'
import MediaForm from 'components/organisms/media-form'
import FlexibleDialog from 'components/molecules/flexible-dialog'
import GpsrDialogForm from 'components/molecules/gpsr-dialog-form'

const ControlsContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.sizings.lvl2};
`
const SubmitButton = styled(Button)`
  margin-left: auto;
`

function CarLayersForm({
  className,
  carPhotoLayers,
  getCarPhotoLayersForCarFile,
  carFile,
  onSubmit,
  carId,
}) {
  const { t } = useTranslation()
  const { vestiging_id: locationId, all_layers: initialPhotoOverlayAllPhotos } =
    carFile

  const [photoOverlayAllPhotos, setPhotoOverlayAllPhotos] = useState(
    initialPhotoOverlayAllPhotos,
  )
  const [selectedLayer, setSelectedLayer] = useState()

  const [dontShowGPSRMessageAgain, setDontShowGPSRMessageAgain] = useState(
    carFile?.gpsrMessageOff || false,
  )

  const {
    isGpsrDialogOpen,
    formikRef,
    handleCloseGpsrDialog,
    handleGpsrFormSubmit,
  } = useGpsrDialog(carFile, carId)

  const handleFormSubmitWithCheckbox = () => {
    if (formikRef.current) {
      const formValues = formikRef.current.values
      const valuesWithCheckbox = {
        ...formValues,
        dontShowGPSRMessageAgain,
      }
      handleGpsrFormSubmit(valuesWithCheckbox)
    }
  }

  useEffect(() => {
    if (!selectedLayer && carFile.layer_id) {
      setSelectedLayer(carFile.layer_id)
    }
  }, [selectedLayer, carFile])

  useEffect(() => {
    getCarPhotoLayersForCarFile({
      id: locationId,
      vestiging_id: locationId,
    })
  }, [getCarPhotoLayersForCarFile, locationId])

  if (carPhotoLayers && !carPhotoLayers[locationId]) {
    return (
      <Typography type="ExplanationParagraph">{t('noLayersYet')}</Typography>
    )
  }

  if (!carPhotoLayers || carPhotoLayers[locationId].loading) {
    return <LoadingIndicator />
  }

  const handleSubmit = () =>
    onSubmit({
      layer_id: selectedLayer,
      all_layers: photoOverlayAllPhotos,
    })

  return (
    <>
      <ControlsContainer>
        <LabeledCheckbox
          checked={photoOverlayAllPhotos}
          id="photoOverlayAllPhotos"
          onChange={setPhotoOverlayAllPhotos}
          label={t('photoOverlayAllPhotos')}
        />
      </ControlsContainer>
      {carPhotoLayers[locationId].data.overlays.length < 1 ? (
        <Typography type="ExplanationParagraph">{t('noLayersYet')}</Typography>
      ) : (
        selectedLayer !== undefined && (
          <MediaForm
            className={className}
            type="images"
            uploadButtonText="Select"
            onChange={(val) => val[0] !== undefined && setSelectedLayer(val[0])}
            exclusiveSelect
            showLabelsOnImages
            items={carPhotoLayers[locationId].data.overlays.map((layer) => ({
              id: layer.id,
              selected: layer.id === selectedLayer,
              name: layer.omschrijving,
              src: layer.url,
              type: 'image',
            }))}
          />
        )
      )}
      <SubmitButton
        type="submit"
        onClick={handleSubmit}
        text={t('saveChanges')}
        level="cta"
      />

      <FlexibleDialog
        open={isGpsrDialogOpen}
        closeHandler={handleCloseGpsrDialog}
        title={t('gpsrForm.addGpsrDetails')}
        content={
          <GpsrDialogForm ref={formikRef} onSubmit={handleGpsrFormSubmit} />
        }
        cancel
        cancelText={t('cancel')}
        onCancel={handleCloseGpsrDialog}
        submitText={t('save')}
        onSubmit={handleFormSubmitWithCheckbox}
        scrollableContent
        additionalFooterContent={
          <Checkbox
            name="dontShowGPSRMessageAgain"
            onChange={setDontShowGPSRMessageAgain}
            title={t('gpsrForm.dontShowGPSRMessageAgain')}
            checked={dontShowGPSRMessageAgain}
          />
        }
      />
    </>
  )
}

CarLayersForm.propTypes = {
  className: PropTypes.string,
  carPhotoLayers: PropTypes.object,
  getCarPhotoLayersForCarFile: PropTypes.func.isRequired,
  carFile: PropTypes.object.isRequired,
  onSubmit: PropTypes.func,
  carId: PropTypes.string,
}

CarLayersForm.defaultProps = {
  className: '',
  carPhotoLayers: null,
  onSubmit: PropTypes.func,
  carId: null,
}

export default withApolloV4Provider(CarLayersForm)
